import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ManagerOnEmployeePortalProps {
  /** Email de la session connectée. */
  email: string | null;
  /** Prénom et nom du profil gérant, quand la lecture a abouti. */
  displayName: string | null;
  onGoToDashboard: () => void;
  onLogout: () => void;
}

/**
 * Affiché par `EmployeeProvider` quand la session connectée est celle d'un
 * gérant (rôle franchisé, aucune fiche salarié) alors que l'URL est l'espace
 * salarié. Le cas réel : un lien « Voir mes horaires » ouvert sur un appareil
 * où le gérant est resté connecté — téléphone d'un proche, poste du magasin.
 *
 * On renvoyait silencieusement vers /dashboard ; la personne se retrouvait
 * devant le tableau de bord du gérant sans un mot, et le vivait comme
 * « j'ai accès à son compte ». Ici on nomme le compte, et on laisse choisir.
 * « Se déconnecter » ferme toutes les sessions du compte, même portée que le
 * bouton de déconnexion habituel : une session gérant sur le mauvais appareil
 * n'a pas à survivre ailleurs.
 */
export const ManagerOnEmployeePortal = ({
  email,
  displayName,
  onGoToDashboard,
  onLogout,
}: ManagerOnEmployeePortalProps) => {
  const accountLabel = displayName ?? email ?? "un compte gérant";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto w-14 h-14 rounded-full bg-muted flex items-center justify-center">
          <ShieldAlert className="w-7 h-7 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">
            Vous êtes connecté en tant que gérant
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Cette page est l'espace salarié d'Equiply. Le compte connecté sur cet
            appareil est celui de <strong className="text-foreground">{accountLabel}</strong>
            {displayName && email ? <> ({email})</> : null}, un compte gérant.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Si vous êtes salarié(e), déconnectez-vous puis connectez-vous avec
            votre propre adresse email.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <Button onClick={onLogout}>Se déconnecter</Button>
          <Button variant="outline" onClick={onGoToDashboard}>
            Aller à mon espace gérant
          </Button>
        </div>
      </div>
    </div>
  );
};
